import React from 'react';
import { View, Text } from 'react-native';
import useMediumScreen from 'hooks/useMediumScreen';
import { useTranslation } from 'react-i18next';
import { Icon } from 'react-native-elements';
import AppContext from 'contexts/AppContext';
import OrderContext from 'contexts/OrderContext';
import { Formik } from 'formik';
import { styles } from 'styles/screens/cloverConnectionReportsScreen';
import UserRoleType from 'types/enums/UserRoleType';
import CloverConnectionReportsForm from 'forms/CloverConnectionReportsForm';
import CloverPoint from 'types/models/CloverPoint';
import CloverConnectionReportsGraphics from 'components/CloverConnectionReportsGraphics';
import { getCloverPointsByCodes } from 'services/paymentsServices';
import CloverPointsList from 'components/CloverPointsList';
import CloverConnectionsForm from 'types/forms/CloverConnectionsForm';
import { colors } from '../../config';
import { Loader } from 'components/Loading';
import { useRoute, RouteProp, useFocusEffect } from '@react-navigation/native';
import { ScrollView } from 'react-native-gesture-handler';
import { MaterialIcons } from '@expo/vector-icons';

export default (): JSX.Element => {
  const [cloverPoints, setCloverPoints] = React.useState<CloverPoint[]>([]);
  const [buttonWasPressed, setButtonWasPressed] = React.useState<boolean>(false);
  const mediumScreen = useMediumScreen();
  const { t } = useTranslation();

  const { setDisplayMenuModal, authData } = React.useContext(AppContext);
  const { currentVenue } = React.useContext(OrderContext);
  const route =
    useRoute<RouteProp<Record<string, { commerceCode: string; venueCode: string; connectionStatus: string }>>>();

  const { commerceCode, venueCode, connectionStatus } = route.params || {};
  const isCommerceEditor = authData?.user.role === UserRoleType.CommerceEditor;

  useFocusEffect(
    React.useCallback(() => {
      if (!commerceCode && !venueCode) {
        setCloverPoints([]);
        setButtonWasPressed(false);
      }
    }, [commerceCode, venueCode]),
  );

  return (
    <ScrollView style={styles.container}>
      <View style={mediumScreen ? styles.header : styles.headerSmall}>
        <Icon
          tvParallaxProperties={null}
          name="menu-outline"
          type="ionicon"
          color="#000000"
          size={40}
          containerStyle={mediumScreen ? styles.iconLarge : styles.iconSmall}
          onPress={() => setDisplayMenuModal(true)}
        />
        {mediumScreen ? (
          <View style={styles.upperTextContainerLarge}>
            <Text style={styles.textTitleLarge}>
              {mediumScreen ? t('clover-connection-status.reports') : currentVenue?.name}
            </Text>
          </View>
        ) : (
          <Text style={styles.textSmall}>
            {mediumScreen ? t('clover-connection-status.reports') : currentVenue?.name}
          </Text>
        )}
        {!mediumScreen && (
          <Text
            style={{
              flexDirection: 'row',
              justifyContent: 'flex-start',
              alignSelf: 'flex-start',
              marginLeft: 40,
              marginBottom: 10,
              fontSize: 16,
              fontWeight: '500',
              lineHeight: 24,
              letterSpacing: 0,
              textAlign: 'left',
            }}
          >
            {t('clover-connection-status.reports')}
          </Text>
        )}
      </View>

      <View style={mediumScreen ? styles.wrapper : styles.wrapperSmall}>
        <Formik<CloverConnectionsForm>
          initialValues={{
            codes: [],
            connectionStatuses: [],
            connectionTypes: [],
          }}
          onSubmit={async (values) => {
            setButtonWasPressed(true);
            const response = await getCloverPointsByCodes(values);
            if (response) {
              setCloverPoints(response);
            }
          }}
          validateOnChange={false}
          validateOnBlur={false}
        >
          {({ handleSubmit, setFieldValue, isSubmitting, errors, values }) => (
            <>
              <CloverConnectionReportsForm
                handleSubmit={handleSubmit}
                currentVenue={currentVenue}
                isCommerceEditor={isCommerceEditor}
                setFieldValue={setFieldValue}
                values={values}
                errors={errors}
                isSubmitting={isSubmitting}
                mediumScreen={mediumScreen}
                t={t}
                commerceCode={commerceCode}
                venueCode={venueCode}
                connectionStatus={connectionStatus}
              />

              {cloverPoints.length > 0 && (
                <View style={{ width: '100%' }}>
                  <CloverConnectionReportsGraphics cloverPoints={cloverPoints} mediumScreen={mediumScreen} />
                  <CloverPointsList
                    cloverPoints={cloverPoints}
                    appColor={currentVenue?.appColor}
                    mediumScreen={mediumScreen}
                    currentVenue={currentVenue}
                    t={t}
                  />
                </View>
              )}
              {isSubmitting && <Loader position="top-center" />}
              {buttonWasPressed && cloverPoints.length === 0 && !isSubmitting && (
                <View
                  style={{
                    width: 330,
                    marginVertical: 30,
                    padding: 10,
                    borderRadius: 10,
                    alignItems: 'center',
                    justifyContent: 'center',
                    alignSelf: 'center',
                  }}
                >
                  <MaterialIcons name="search-off" type="ionicon" color={colors.black} size={45} />
                  <Text
                    style={{
                      fontSize: mediumScreen ? 20 : 15,
                      fontWeight: '500',
                      textAlign: 'center',
                      color: colors.black,
                    }}
                  >
                    {t('clover-connection-status.no-clover-points-found')}
                  </Text>
                </View>
              )}
            </>
          )}
        </Formik>
      </View>
    </ScrollView>
  );
};
